/* src/contexts/profile/profileAuth.js */
import { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebaseConfig'; // Adjust path if needed

/**
 * Custom hook to listen for Firebase auth state changes.
 * Provides the authenticated user object, a flag indicating if the initial
 * auth state has been resolved, and a flag indicating if the user ID changed.
 *
 * @returns {{
 *  authUser: object | null,       // The Firebase user object or null
 *  isAuthResolved: boolean,      // True once the listener has fired at least once
 *  authUserIdChanged: boolean,  // True if the UID changed from the previous state
 * }}
 */
export const useAuthStateListener = () => {
    // Initialize authUser to null, rely on listener for the definite state
    const [authUser, setAuthUser] = useState(null);
    // NEW STATE: Track if the listener has run at least once to resolve initial state
    const [isAuthResolved, setIsAuthResolved] = useState(false);
    // Flag to signal when the UID specifically changes (login/logout)
    const [authUserIdChanged, setAuthUserIdChanged] = useState(false);
    // Ref to store the previous UID for comparison
    const previousUidRef = useRef(null);
    // Ref to prevent setting resolved state multiple times unnecessarily
    const resolvedStateSet = useRef(false);

    useEffect(() => {
        // console.log("[useAuthStateListener] Setting up Firebase auth listener."); // Optional debug log

        // Reset resolved state flag when the effect re-runs (though it shouldn't with empty deps)
        resolvedStateSet.current = false;

        const listener = onAuthStateChanged(auth, (user) => {
            const currentUid = user?.uid || null;
            // console.log(`[useAuthStateListener] Auth state changed. User: ${user ? user.uid : 'null'}. Previous UID: ${previousUidRef.current}`); // Optional debug log

            // Check if the User ID itself has changed (login/logout)
            if (currentUid !== previousUidRef.current) {
                // console.log(`[useAuthStateListener] Auth ID changed: ${previousUidRef.current || 'none'} -> ${currentUid || 'none'}`); // Optional debug log
                setAuthUserIdChanged(true); // Signal the change for context resets
            } else {
                setAuthUserIdChanged(false); // Reset if ID is the same (e.g., token refresh)
            }

            setAuthUser(user); // Update the user state
            previousUidRef.current = currentUid; // Update the ref for the *next* comparison

            // Mark auth as resolved *after* the first callback runs.
            // Use the ref to ensure it only happens once per listener setup.
            if (!resolvedStateSet.current) {
                // console.log("[useAuthStateListener] Auth state has been resolved."); // Optional debug log
                setIsAuthResolved(true);
                resolvedStateSet.current = true; // Mark as set
            }
        });

        // Cleanup function to unsubscribe the listener
        return () => {
            // console.log("[useAuthStateListener] Cleaning up Firebase auth listener."); // Optional debug log
            listener(); // Unsubscribe
            // Optionally reset state on cleanup if needed, although re-mount will re-initialize
            // setIsAuthResolved(false);
            // setAuthUser(null);
            // previousUidRef.current = null;
        };
    }, []); // Empty dependency array: run only once on mount

    // Return the auth user, the resolved status, and the change flag
    return { authUser, isAuthResolved, authUserIdChanged };
};